import { auth } from "@/lib/firebase";
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

interface ApiResponse<T> {
  statusCode: number;
  message?: string;
  data: T;
}

interface BatchUserData {
  userID: string;
  companyUserData: any;
  userData: any;
  policies: Array<{
    id: string;
    reference?: string;
    completed?: string;
    dateAssigned?: string;
    hasQuiz: boolean;
    policyName: string;
    quizResults: any;
  }>;
}

interface BatchResponse {
  companyInfo: any;
  policyNameMap: Record<string, string>;
  policyQuizMap: Record<string, boolean>;
  users: BatchUserData[];
}

/**
 * Service for handling user reports data and operations
 * Uses Thalassa API for all data operations
 */
export const userReportsService = {
  /**
   * BATCH: Fetch all user reports data in a single request
   * This is the recommended method - reduces N*M API calls to just 1
   */
  async getBatchUserReports(companyId: string): Promise<BatchResponse | null> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<BatchResponse>>(
        `/v1/user-reports/batch`,
        { backend: 'thalassa', companyID: companyId }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching batch user reports:", error);
      return null;
    }
  },
  /**
   * Get the company ID for the current user
   */
  async getCurrentUserCompanyId(): Promise<string | null> {
    try {
      if (!auth.currentUser) return null;
      
      const response = await deiterateAPI.GET<ApiResponse<{ companyId: string | null }>>(
        `/v1/user-reports/user/${auth.currentUser.uid}/company-id`,
        { backend: 'thalassa' }
      );
      return response.data.companyId;
    } catch (error) {
      console.error("Error getting company ID:", error);
      return null;
    }
  },

  /**
   * Get company information by ID
   */
  async getCompanyInfo(companyId: string): Promise<Record<string, any> | null> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<Record<string, any>>>(
        `/v1/user-reports/company/${companyId}/info`,
        { backend: 'thalassa', companyID: companyId }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting company info:", error);
      return null;
    }
  },

  /**
   * Get all users for a company
   */
  async getCompanyUsers(companyId: string): Promise<any[]> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<any[]>>(
        `/v1/user-reports/company/${companyId}/users`,
        { backend: 'thalassa', companyID: companyId }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting company users:", error);
      return [];
    }
  },

  /**
   * Get user information by ID
   */
  async getUserInfo(userId: string): Promise<Record<string, any> | null> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<Record<string, any> | null>>(
        `/v1/user-reports/user/${userId}/info`,
        { backend: 'thalassa' }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting user info:", error);
      return null;
    }
  },

  /**
   * Get assigned policies for a user
   */
  async getUserPolicies(userId: string): Promise<any[]> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<any[]>>(
        `/v1/user-reports/user/${userId}/policies`,
        { backend: 'thalassa' }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting user policies:", error);
      return [];
    }
  },

  /**
   * Get quiz questions for a policy
   */
  async getQuizQuestions(policyRef: string, companyId: string): Promise<any[]> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<any[]>>(
        `/v1/user-reports/policy/${encodeURIComponent(policyRef)}/quiz-questions`,
        { backend: 'thalassa', companyID: companyId }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting quiz questions:", error);
      return [];
    }
  },

  /**
   * Get quiz attempts for a user's policy
   */
  async getQuizAttempts(userId: string, policyRef: string): Promise<any[]> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<any[]>>(
        `/v1/user-reports/user/${userId}/policy/${encodeURIComponent(policyRef)}/attempts`,
        { backend: 'thalassa' }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting quiz attempts:", error);
      return [];
    }
  },

  /**
   * Process quiz results from attempts (local processing, no API needed)
   */
  processQuizResults(attempts: any[]): {
    fullCorrectAttempts: number;
    percentage: string;
    mostRecent: any | null;
  } {
    if (!attempts || attempts.length === 0) {
      return { 
        fullCorrectAttempts: 0, 
        percentage: "0%",
        mostRecent: null
      };
    }
    
    // Find most recent attempt
    const mostRecent = attempts.reduce((latest, current) => {
      const latestTime = new Date(latest.timestamp);
      const currentTime = new Date(current.timestamp);
      return currentTime > latestTime ? current : latest;
    });
    
    // Count fully correct attempts
    const fullCorrectAttempts = attempts.filter(attempt => {
      return this.checkAnswers(attempt).allCorrect;
    }).length;
    
    // Calculate percentage
    const percentage = Math.round((fullCorrectAttempts / attempts.length) * 100);
    
    return {
      fullCorrectAttempts,
      percentage: `${percentage}%`,
      mostRecent
    };
  },

  /**
   * DED-1239: Check if a policy has a quiz available
   */
  async policyHasQuiz(policyId: string, companyId: string): Promise<boolean> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<{ hasQuiz: boolean }>>(
        `/v1/user-reports/policy/${encodeURIComponent(policyId)}/has-quiz`,
        { backend: 'thalassa', companyID: companyId }
      );
      return response.data.hasQuiz;
    } catch (error) {
      console.error("Error checking if policy has quiz:", error);
      return false;
    }
  },

  /**
   * Get quiz results for a specific policy and user
   */
  async getPolicyQuizResults(
    userId: string, 
    policyId: string,
    companyId: string
  ): Promise<Record<string, any> | null> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<Record<string, any> | null>>(
        `/v1/user-reports/user/${userId}/policy/${encodeURIComponent(policyId)}/quiz-results`,
        { backend: 'thalassa', companyID: companyId }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting policy quiz results:", error);
      return null;
    }
  },

  /**
   * Check if answers in a quiz attempt are correct (local processing, no API needed)
   */
  checkAnswers(data: any): {
    correctCount: number;
    totalQuestions: number;
    allCorrect: boolean;
  } {
    let correctCount = 0;
    let totalQuestions = 0;
    
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const match = key.match(/^(\d+)GivenAnswer$/);
        if (match) {
          const questionNumber = match[1];
          const correctAnswer = data[`${questionNumber}CorrectAnswer`];
          const givenAnswer = data[key];
          
          if (correctAnswer !== undefined && givenAnswer !== undefined) {
            totalQuestions++;
            if (correctAnswer === givenAnswer) {
              correctCount++;
            }
          }
        }
      }
    }
    
    const allCorrect = totalQuestions > 0 && correctCount === totalQuestions;
    
    return {
      correctCount,
      totalQuestions,
      allCorrect
    };
  }
};
